import { queryClient } from '@/queryClient'
import { MutationFunction, UseMutationOptions, UseMutationResult, useMutation } from '@tanstack/react-query'
import { AxiosResponse, ResponseType } from 'axios'
import qs from 'query-string'
import { createApiRequest } from './createApiRequest'

// Definition of mutation options that include url, method, additional API options, and keys to refetch
interface DefinitionMutationOptions extends UseMutationOptions {
    url: string
    method: 'POST' | 'PATCH' | 'DELETE'
    apiOptions?: {
        responseType?: ResponseType
        headers?: Record<string, string>
    }
    keysToRefetch?: string[][]
}

// Definition of options for using the mutation that include query parameters, keys to refetch, and lifecycle hooks
interface UsageMutationOptions<TData> {
    queryParams?: Record<string, string>
    pathParams?: Record<string, string>
    refetch?: string[][]
    onSuccess?: (data: TData, ...arg: unknown[]) => void
    onError?: (...args: unknown[]) => void
    onSettled?: (...args: unknown[]) => void
    onMutate?: (...args: unknown[]) => void
    apiOptions?: {
        responseType?: ResponseType
        headers?: Record<string, string>
        onUploadProgress?: (progressEvent: ProgressEvent) => void
    }
}

// Payload type definition
export type PayloadType = Record<string, unknown> | FormData | undefined

// The createMutation function that takes the definition options and returns a new function that takes usage options
// and returns a mutation result
export const createMutation = <TData>(
    definitionOptions: DefinitionMutationOptions
): ((arg: UsageMutationOptions<TData>) => UseMutationResult<TData, unknown, PayloadType, unknown>) => {
    const useMutationFn = (usageOptions: UsageMutationOptions<TData> = {}) => {
        const { url, keysToRefetch, method = 'POST', apiOptions: definitionApiOptions } = definitionOptions
        const {
            queryParams,
            pathParams = {},
            refetch: usageRefetch,
            apiOptions: usageApiOptions,
            ...restUsageOptions
        } = usageOptions

        // Interpolate path parameters in URL
        let finalUrl = url
        Object.entries(pathParams).forEach(([param, value]) => {
            finalUrl = finalUrl.replace(`:${param}`, value)
        })

        // Merge keys for refetching
        const mergedKeys = mergeKeysToRefetch(keysToRefetch, usageRefetch)
        const params = queryParams ? qs.stringify(queryParams) : ''

        const mergedConfigs = mergeOptions<TData>(restUsageOptions, definitionOptions)

        // Overwrite onSuccess to include invalidation of queries
        const success = mergedConfigs.onSuccess
        mergedConfigs.onSuccess = (...args) => {
            mergedKeys.forEach(key => {
                queryClient.invalidateQueries({
                    predicate: query => {
                        return key.some(k => query.queryKey.includes(k))
                    },
                })
            })

            success?.(...args)
        }
        // Type of the mutation function
        const mutationFn: MutationFunction<TData, PayloadType> = (data?: PayloadType) =>
            createApiRequest<AxiosResponse<TData>>({
                url: finalUrl,
                data,
                method,
                queryParams: params,
                apiOptions: {
                    ...usageApiOptions,
                    ...definitionApiOptions,
                },
            }).then(response => response as TData)

        return useMutation<TData, unknown, PayloadType, unknown>({
            mutationFn,
            ...mergedConfigs,
        })
    }

    return useMutationFn
}

// Merge keys for refetching. If keys exist in both the definition and usage options, they are combined.
const mergeKeysToRefetch = (keysToRefetch?: string[][], refetch?: string[][]) => {
    let mergedKeys: string[][] = []

    if (Array.isArray(keysToRefetch) && keysToRefetch.length > 0) {
        mergedKeys = [...mergedKeys, ...keysToRefetch]
    }

    if (Array.isArray(refetch) && refetch.length > 0) {
        mergedKeys = [...mergedKeys, ...refetch]
    }

    return mergedKeys
}

// Merge usage and definition options, with usage options taking precedence
const mergeOptions = <T>(usageOptions?: UseMutationOptions<T>, definitionOptions?: UseMutationOptions) => {
    const mergedOptions = {
        ...definitionOptions,
        ...usageOptions,
    }

    return mergedOptions as UseMutationOptions<T, unknown, PayloadType, unknown>
}
